import { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';
import { Textfield } from '@src/shared/fields/text-field';
import { ProgramStageMultiSelect } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder/program-stage-multi-select';
import type { TStage } from '@src/shared/types/stage';
import type { TStageProgress } from '@shared/types/stage-progress';

import type { TCreateReminderFormValues } from '../schema';
import { SelectedStageParticipantsPopover } from './selected-stage-participants-popover';

function toMemberIdsKey(ids: number[]) {
  return [...ids].sort((a, b) => a - b).join(',');
}

type BulkStageAudienceSectionProps = {
  stages: TStage[];
};

export function BulkStageAudienceSection({
  stages,
}: BulkStageAudienceSectionProps) {
  const { table } = useTiclickTableContext<TStageProgress>();
  const { setFieldValue } = useFormikContext<TCreateReminderFormValues>();

  const selectedMemberIds = table
    .getSelectedRowModel()
    .rows.flat()
    .map((row) => (row.original as TStageProgress).participant?.id)
    .filter((id): id is number => id != null);

  const selectedMemberIdsKey = toMemberIdsKey(selectedMemberIds);

  useEffect(() => {
    const nextIds = selectedMemberIdsKey
      ? selectedMemberIdsKey.split(',').map(Number)
      : [];

    setFieldValue('selectedMemberIds', nextIds, false);
  }, [selectedMemberIdsKey, setFieldValue]);

  const hasPreselectedParticipants = selectedMemberIds.length > 0;

  return (
    <section className='rounded-xl border border-[#E8E7F0] bg-white p-4'>
      <p className='mb-4 text-sm font-semibold text-gray-500'>
        1. Audience Details
      </p>

      <div className='flex flex-col gap-4'>
        <Textfield
          label='Reminder name'
          name='name'
          placeholder='Enter Reminder Name'
        />

        {hasPreselectedParticipants ? (
          <SelectedStageParticipantsPopover />
        ) : (
          <ProgramStageMultiSelect stages={stages} />
        )}
      </div>
    </section>
  );
}
